import Ruler from "@untitled-ui/icons-react/build/esm/Ruler";
import Plus from "@untitled-ui/icons-react/build/esm/Plus";
import AlertCircle from "@untitled-ui/icons-react/build/esm/AlertCircle";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import BtnLargeSolid from "../../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../../common/checkbox/CheckboxVector";
import FormField from "../../../../common/forms/FormField";
import FullScreenOverlay from "../../../../common/overlay/FullScreenOverlay";
import TooltipSimple from "../../../../common/tooltip/TooltipSimple";
import { getMissingSizeRows, getSizeMappingConflicts } from "../sizeGroupConversionState";

const EMPTY_ARRAY = [];

const StyledPopup = styled.div`
    width: 620px;
    text-align: start;
    .shell {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 9px 0 17px;
        border-bottom: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
    }
    .title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: 14px;
    }
    .icon {
        width: 16px;
        height: 16px;
    }
    .actions {
        display: flex;
        gap: 8px;
    }
    .sizes-grid-row {
        display: grid;
        grid-template-columns: 36px minmax(0, 1fr) minmax(0, 1fr) 200px;
        gap: 10px;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
        padding: 8px;
    }
    .sizes-column-title {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
    }
    .bulk-action-row {
        display: grid;
        grid-template-columns: 1fr 200px 29px;
        gap: 10px;
        align-items: center;
        padding: 0 8px;
    }
    .bulk-label {
        color: var(--text-and-icons-gray-300, #4f5b6d);
        text-align: right;
        font-size: 14px;
    }
    .bulk-add-btn {
        background: var(--buttons-all-platforms-gray-500, #10141b);
        border-radius: 4px;
        width: 29px;
        height: 27px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .bulk-add-btn.enabled:hover {
        box-shadow: 0 0 0 1px #ff8c00;
    }
    .plus {
        width: 16px;
        height: 16px;
    }
    .sizes-table-scroll {
        max-height: 620px;
        overflow-y: auto;
    }
    .sizes-table-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .sizes-table-item {
        border-radius: 4px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
    }
    .sizes-table-item:nth-child(odd) {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
    }
    .sizes-item-name {
        min-width: 0;
        overflow-wrap: anywhere;
    }
    .sizes-checkbox-cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
    .sizes-table-heading {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-bottom: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
    }
    .empty {
        padding: 16px 8px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
    }
    .warning {
        color: var(--text-and-icons-orange-500, #ff9900);
        text-align: center;
        font-size: 12px;
    }
    .sizes-conflict-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
    }
    .sizes-conflict-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: var(--text-and-icons-orange-500, #ff9900);
    }
    .sizes-pos-size-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
    }
    .sizes-pos-size-field {
        flex: 1;
        min-width: 0;
    }

    /* Keep the natural height, with room to scroll on short viewports. */
    & {
        display: flex;
        flex-direction: column;
        min-height: 0;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
    }

    .shell {
        min-height: 0;
        overflow-y: auto;
    }

    .shell > * {
        flex-shrink: 0;
    }

    .sizes-table-scroll {
        flex-shrink: 1;
        /* Retain a readable scroll target if controls exceed the viewport. */
        min-height: 44px;
    }
`;

export default function POSSizeGroupConversionPopup({
    show,
    onClose,
    categoryFieldName,
    categorySourceRows = EMPTY_ARRAY,
    relatedSizeGroupMappings = EMPTY_ARRAY,
    conversions = EMPTY_ARRAY,
    setConversions,
    setSaveButtonEnabled,
    posExportRequestData
}) {
    const rows = useMemo(
        () =>
            getMissingSizeRows({
                categoryFieldName,
                categorySourceRows,
                relatedSizeGroupMappings,
                labelBrandProducts: posExportRequestData?.labelBrandProducts,
                sizeGroupsFromPOS: posExportRequestData?.sizeGroupsFromPOS
            }),
        [categoryFieldName, categorySourceRows, relatedSizeGroupMappings, posExportRequestData]
    );
    const [draft, setDraft] = useState([]);
    const [bulkText, setBulkText] = useState("");
    const [bulkChangeEnabled, setBulkChangeEnabled] = useState(false);

    useEffect(() => {
        if (!show) return;
        setDraft(
            rows.map(row => ({
                ...row,
                isChecked: false,
                exportName:
                    conversions
                        .find(group => group?.sizeGroupName === row.sizeGroupName)
                        ?.sizes?.find(size => size?.labelBrandName === row.labelBrandName)?.exportName || ""
            }))
        );
        setBulkText("");
        setBulkChangeEnabled(false);
    }, [show, rows, conversions]);

    const posOptionsByGroup = useMemo(
        () =>
            new Map(
                (posExportRequestData?.sizeGroupsFromPOS || EMPTY_ARRAY).map(group => [
                    group?.sizeGroupName,
                    (Array.isArray(group?.sizeNames) ? group.sizeNames : EMPTY_ARRAY).map((name, index) => ({
                        id: `${group?.sizeGroupID ?? group?.sizeGroupName}-${index}`,
                        name,
                        value: name
                    }))
                ])
            ),
        [posExportRequestData]
    );
    const hasChecked = draft.some(row => row.isChecked);
    const allChecked = draft.length > 0 && draft.every(row => row.isChecked);
    const conflicts = useMemo(
        () =>
            getSizeMappingConflicts({
                categoryFieldName,
                categorySourceRows,
                relatedSizeGroupMappings,
                labelBrandProducts: posExportRequestData?.labelBrandProducts,
                sizeGroupsFromPOS: posExportRequestData?.sizeGroupsFromPOS,
                mappings: draft
            }),
        [categoryFieldName, categorySourceRows, relatedSizeGroupMappings, posExportRequestData, draft]
    );
    const hasConflicts = conflicts.size > 0;
    const complete = !hasConflicts && (draft.length === 0 || (!hasChecked && draft.every(row => !!row.exportName)));
    const updateRow = (rowIndex, exportName) =>
        setDraft(current => current.map((row, index) => (index === rowIndex ? { ...row, exportName } : row)));
    const sharedBulkOptions = useMemo(() => {
        const selectedRows = draft.filter(row => row.isChecked);
        if (!selectedRows.length) return EMPTY_ARRAY;
        const selectedOptions = selectedRows.map(row => posOptionsByGroup.get(row.sizeGroupName) || EMPTY_ARRAY);
        return selectedOptions[0].filter(option =>
            selectedOptions.every(options => options.some(item => item.name === option.name))
        );
    }, [draft, posOptionsByGroup]);
    const canApplyBulk = hasChecked && bulkChangeEnabled && sharedBulkOptions.some(option => option.name === bulkText);
    const applyBulkMapping = () => {
        if (!canApplyBulk) return;
        setDraft(current =>
            current.map(row => (row.isChecked ? { ...row, exportName: bulkText, isChecked: false } : row))
        );
        setBulkText("");
        setBulkChangeEnabled(false);
    };

    const save = () => {
        if (!complete) return;
        const byGroup = new Map();
        draft.forEach(row => {
            if (!byGroup.has(row.sizeGroupName)) byGroup.set(row.sizeGroupName, []);
            byGroup.get(row.sizeGroupName).push({ labelBrandName: row.labelBrandName, exportName: row.exportName });
        });
        const next = [...byGroup].map(([sizeGroupName, sizes]) => ({ sizeGroupName, sizes }));
        setConversions?.(next);
        setSaveButtonEnabled?.(true);
        onClose?.();
    };

    return (
        <FullScreenOverlay show={show}>
            <StyledPopup>
                <div className="shell">
                    <div className="header">
                        <div className="title">
                            <Ruler className="icon" />
                            CUSTOMIZE SIZES
                        </div>
                        <div className="actions">
                            <BtnLargeSolid
                                title="CLOSE"
                                onClick={onClose}
                                normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                style={{ width: 67, height: 30 }}
                                textStyle={{ fontSize: 12 }}
                            />
                            {rows.length > 0 && (
                                <BtnLargeSolid
                                    title="SAVE"
                                    onClick={save}
                                    disabled={!complete}
                                    normalColor="var(--buttons-all-platforms-orange-500, #FF9800)"
                                    disabledColor="var(--Buttons-All-Platforms-Gray-500)"
                                    style={{ width: 67, height: 30 }}
                                    textStyle={{ fontSize: 12 }}
                                />
                            )}
                        </div>
                    </div>
                    {rows.length ? (
                        <>
                            <div className="bulk-action-row">
                                <div
                                    className="bulk-label"
                                    style={{
                                        color: hasChecked ? "var(--text-and-icons-gray-100, #d1d1d1)" : undefined
                                    }}>
                                    Map selected Brand sizes to POS size:
                                </div>
                                <FormField
                                    type="dropdown"
                                    dropdownWithinViewport={true}
                                    varName="size-bulk-change"
                                    styling={{
                                        formFieldWidth: "100%",
                                        formFieldHeight: "27px",
                                        marginBottom: "0px",
                                        dropdownOverlayTop: "35px"
                                    }}
                                    placeholder="Select yours"
                                    isDropdownMultiselect={false}
                                    isDropdownSearchable={true}
                                    useDropdownPortal={true}
                                    dropdownData={sharedBulkOptions}
                                    value={hasChecked ? bulkText : ""}
                                    onDropdownChange={item => {
                                        setBulkText(item?.name || "");
                                        setBulkChangeEnabled(true);
                                    }}
                                    requiredType={hasChecked ? "simple" : "disabledSimple"}
                                />
                                <div
                                    className={`bulk-add-btn ${canApplyBulk ? "enabled cursor-pointer" : "disabled hover-not-allowed"}`}
                                    onClick={applyBulkMapping}>
                                    <Plus
                                        className="plus"
                                        color={canApplyBulk ? "#FFFFFF" : "var(--text-and-icons-gray-300, #7C868B)"}
                                    />
                                </div>
                            </div>
                            {hasConflicts ? (
                                <div className="sizes-conflict-message" role="alert">
                                    <AlertCircle className="sizes-conflict-icon" aria-hidden="true" />
                                    <span>
                                        It was detected that different sizes on the same brand's product were mapped to
                                        the same POS size. This is not possible. Choose a different POS size for each
                                        variant, or change the POS Size System in Categories. Resolve the highlighted
                                        mappings before saving.
                                    </span>
                                </div>
                            ) : (
                                !complete && (
                                    <div className="warning" role="status">
                                        {hasChecked && !sharedBulkOptions.length
                                            ? "The selected POS size systems have no sizes in common. Map these rows separately."
                                            : "SAVE is only enabled when all missing brand's sizes are linked to a POS size!"}
                                    </div>
                                )
                            )}
                            <div className="sizes-table-scroll">
                                <div className="sizes-table-heading sizes-column-title sizes-grid-row">
                                    <div className="sizes-checkbox-cell">
                                        <CheckboxVector
                                            checkboxColor="#00F260"
                                            isChecked={allChecked}
                                            onCheckboxClick={() =>
                                                setDraft(current =>
                                                    current.map(row => ({ ...row, isChecked: !allChecked }))
                                                )
                                            }
                                        />
                                    </div>
                                    <div>POS Size System</div>
                                    <div>Missing Brand's size</div>
                                    <div>POS Size</div>
                                </div>
                                <div className="sizes-table-body">
                                    {draft.map((row, index) => (
                                        <div
                                            className="sizes-grid-row sizes-table-item"
                                            key={JSON.stringify([row.sizeGroupName, row.labelBrandName])}>
                                            <div className="sizes-checkbox-cell">
                                                <CheckboxVector
                                                    checkboxColor="#00F260"
                                                    isChecked={row.isChecked}
                                                    onCheckboxClick={() =>
                                                        setDraft(current =>
                                                            current.map((item, i) =>
                                                                i === index
                                                                    ? { ...item, isChecked: !item.isChecked }
                                                                    : item
                                                            )
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="sizes-item-name">{row.sizeGroupName}</div>
                                            <div className="sizes-item-name">{row.labelBrandName}</div>
                                            <div className="sizes-pos-size-cell">
                                                <div className="sizes-pos-size-field">
                                                    <FormField
                                                        type="dropdown"
                                                        dropdownWithinViewport={true}
                                                        varName={`pos-size-${index}`}
                                                        value={row.exportName}
                                                        requiredType="none"
                                                        isDropdownMultiselect={false}
                                                        isDropdownSearchable={true}
                                                        placeholder="Select yours"
                                                        useDropdownPortal={true}
                                                        dropdownData={
                                                            posOptionsByGroup.get(row.sizeGroupName) || EMPTY_ARRAY
                                                        }
                                                        onDropdownChange={item => updateRow(index, item?.name || "")}
                                                        styling={{
                                                            formFieldWidth: "100%",
                                                            formFieldHeight: "27px",
                                                            marginBottom: "0px",
                                                            dropdownOverlayTop: "35px"
                                                        }}
                                                    />
                                                </div>
                                                {conflicts.has(
                                                    JSON.stringify([row.sizeGroupName, row.labelBrandName])
                                                ) && (
                                                    <TooltipSimple title="Different sizes on the same brand's product cannot share this POS size.">
                                                        <AlertCircle
                                                            className="sizes-conflict-icon"
                                                            role="img"
                                                            aria-label="Conflicting POS size mapping"
                                                        />
                                                    </TooltipSimple>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="empty" role="status">
                            All brand sizes are available in their assigned POS size systems. No size mappings are
                            needed.
                        </div>
                    )}
                </div>
            </StyledPopup>
        </FullScreenOverlay>
    );
}
